import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc,
  arrayUnion,
  arrayRemove 
} from 'firebase/firestore';
import { db } from '../firebase';

// Get user's connections from their profile
export const getUserConnections = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.network || [];
    }
    return [];
  } catch (error) {
    console.error('Error getting user connections:', error);
    throw error;
  }
};

// Get attendees for an event that are in the user's network
export const getConnectedAttendees = async (eventId, userId) => {
  try {
    // Get user's connections
    const userConnections = await getUserConnections(userId);
    
    if (userConnections.length === 0) {
      return [];
    }
    
    // Get event registrations
    const registrationsQuery = query(
      collection(db, 'eventRegistrations'),
      where('eventId', '==', eventId)
    );
    
    const registrationsSnapshot = await getDocs(registrationsQuery);
    const allRegistrations = registrationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter registrations to only include connected users
    const connectedAttendees = [];
    
    for (const registration of allRegistrations) {
      // Check if this attendee is in the user's network
      const isConnected = userConnections.some(connection => 
        connection.userId === registration.userId || 
        connection.id === registration.userId
      );
      
      if (isConnected) {
        // Get the attendee's profile for display
        try {
          const attendeeDoc = await getDoc(doc(db, 'users', registration.userId));
          if (attendeeDoc.exists()) {
            const attendeeData = attendeeDoc.data();
            connectedAttendees.push({
              ...registration,
              profile: {
                firstName: attendeeData.firstName,
                lastName: attendeeData.lastName,
                title: attendeeData.jobTitle,
                company: attendeeData.company,
                profilePhoto: attendeeData.profilePhoto,
                membershipTier: attendeeData.membershipTier
              }
            });
          }
        } catch (profileError) {
          console.error('Error fetching attendee profile:', profileError);
          // Still include the registration without profile details
          connectedAttendees.push(registration);
        }
      }
    }
    
    return connectedAttendees;
  } catch (error) {
    console.error('Error getting connected attendees:', error);
    throw error;
  }
};

// Add a connection to user's network
export const addConnection = async (userId, connectionData) => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Add connection to user's network
    await updateDoc(userRef, {
      network: arrayUnion({
        userId: connectionData.userId,
        id: connectionData.id || connectionData.userId,
        name: connectionData.name,
        title: connectionData.title,
        company: connectionData.company,
        connectedAt: new Date(),
        mutualConnections: connectionData.mutualConnections || 0
      })
    });
    
    return true;
  } catch (error) {
    console.error('Error adding connection:', error);
    throw error;
  }
};

// Remove a connection from user's network
export const removeConnection = async (userId, connectionId) => {
  try {
    const userConnections = await getUserConnections(userId);
    const connectionToRemove = userConnections.find(conn => 
      conn.userId === connectionId || conn.id === connectionId
    );
    
    if (connectionToRemove) {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        network: arrayRemove(connectionToRemove)
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error removing connection:', error);
    throw error;
  }
};

// Check if two users are connected
export const areUsersConnected = async (userId1, userId2) => {
  try {
    const user1Connections = await getUserConnections(userId1);
    return user1Connections.some(connection => 
      connection.userId === userId2 || connection.id === userId2
    );
  } catch (error) {
    console.error('Error checking connection status:', error);
    return false;
  }
};

// Get mutual connections between two users
export const getMutualConnections = async (userId1, userId2) => {
  try {
    const [user1Connections, user2Connections] = await Promise.all([
      getUserConnections(userId1),
      getUserConnections(userId2)
    ]);
    
    const mutualConnections = [];
    
    user1Connections.forEach(conn1 => {
      const mutual = user2Connections.find(conn2 => 
        conn1.userId === conn2.userId || conn1.id === conn2.id
      );
      if (mutual) {
        mutualConnections.push(conn1);
      }
    });
    
    return mutualConnections;
  } catch (error) {
    console.error('Error getting mutual connections:', error);
    return [];
  }
};

// Get connection statistics for a user
export const getConnectionStats = async (userId) => {
  try {
    const connections = await getUserConnections(userId);
    
    // Group by industry/company for insights
    const byIndustry = {};
    const byCompany = {};
    
    connections.forEach(connection => {
      if (connection.industry) {
        byIndustry[connection.industry] = (byIndustry[connection.industry] || 0) + 1;
      }
      if (connection.company) {
        byCompany[connection.company] = (byCompany[connection.company] || 0) + 1;
      }
    });
    
    return {
      totalConnections: connections.length,
      byIndustry,
      byCompany,
      recentConnections: connections
        .filter(conn => conn.connectedAt)
        .sort((a, b) => new Date(b.connectedAt) - new Date(a.connectedAt))
        .slice(0, 5)
    };
  } catch (error) {
    console.error('Error getting connection stats:', error);
    return {
      totalConnections: 0,
      byIndustry: {},
      byCompany: {},
      recentConnections: []
    };
  }
};

export default {
  getUserConnections,
  getConnectedAttendees,
  addConnection,
  removeConnection,
  areUsersConnected,
  getMutualConnections,
  getConnectionStats
};
